/* GiochiMontessori.it — carosello video UGC (sections/gm-ugc-video.liquid)

   Unico modulo GM con JavaScript: la riproduzione video non si fa in CSS.
   Vanilla, nessuna libreria, custom element autonomo per sezione.

   Regole implementate:
   · un solo video alla volta — riprodurne uno mette in pausa l'altro
   · la sorgente si carica al primo tap (preload="none"), non al caricamento
   · la barra di avanzamento segue timeupdate, non un valore finto
   · muted sempre: nessun suono senza un gesto esplicito

   ── Il principio che regge tutto il file ───────────────────────────────────
   Lo stato visibile della card (data-gm-playing, che in CSS scambia il
   triangolo col simbolo di pausa, e aria-pressed) segue gli EVENTI del
   video, non la nostra intenzione di farlo partire.

   Sembra un dettaglio ed è il cuore: video.play() può essere rifiutato — su
   iOS quando i dati non sono ancora pronti, ovunque quando un caricamento è
   in corso. Scrivendo l'attributo a mano dentro toggle(), come si faceva
   prima, bastava un rifiuto per lasciare la card accesa col simbolo di pausa
   sopra un fotogramma fermo: l'interfaccia dichiarava "sto riproducendo" per
   un video in pausa. Legandolo a play/pause/ended l'incoerenza non è più
   rappresentabile.

   ── Le due cose scritte così per colpa di iOS ─────────────────────────────
   1. La sorgente si assegna a video.src, non a un <source> figlio seguito da
      video.load(). Un <video> che nasce con un <source> senza src è un
      elemento in errore (NETWORK_NO_SOURCE) già al caricamento della pagina,
      e su Safari il load() successivo al tap non sempre lo riporta indietro.
      Assegnare src fa partire il caricamento da solo: load() non serve.

   2. play() si chiama SUBITO, nello stesso gesto del tap: su iOS il permesso
      di riprodurre vale solo dentro il gesto e scade appena si aspetta
      qualcosa. Se viene rifiutato si riprova UNA volta — subito se i dati ci
      sono già (readyState >= 3), altrimenti al primo canplay. Il controllo
      su readyState non è pignoleria: su un video già caricato canplay non
      torna a scattare, quindi aspettarlo e basta significa non riprovare
      mai.
*/
class GmUgc extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-gm-ugc-card]'));
    // la card di cui l'utente ha chiesto la riproduzione per ultima: serve a
    // non far ripartire, con la riprova, un video che nel frattempo ha perso
    // il suo turno.
    this.richiesta = null;

    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;

      card.addEventListener('click', () => {
        if (this.dataset.gmMode === 'lightbox') {
          this.apriLightbox(card, video);
          return;
        }
        this.toggle(card, video);
      });

      video.addEventListener('play', () => {
        // Uno alla volta. Mettere in pausa l'altro fa scattare il suo evento
        // 'pause', che spegne la sua card: nessuno stato da sincronizzare a
        // mano.
        this.cards.forEach((altra) => {
          if (altra === card) return;
          const v = altra.querySelector('video');
          if (v && !v.paused) v.pause();
        });
        card.dataset.gmPlaying = '';
        card.setAttribute('aria-pressed', 'true');
      });

      video.addEventListener('pause', () => {
        delete card.dataset.gmPlaying;
        card.setAttribute('aria-pressed', 'false');
      });

      video.addEventListener('ended', () => {
        const bar = card.querySelector('[data-gm-ugc-progress]');
        if (bar) bar.style.width = '0%';
      });

      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        const bar = card.querySelector('[data-gm-ugc-progress]');
        if (bar) bar.style.width = (video.currentTime / video.duration) * 100 + '%';
      });
    });
  }

  /* Lightbox: un <dialog> per componente, creato al primo tocco. Dentro, un
     <video> nuovo con i controlli nativi e l'audio acceso: il tocco sulla
     card è il gesto esplicito che i browser richiedono per il suono. Alla
     chiusura il video si ferma e perde la sorgente, così non continua a
     scaricare in sottofondo. */
  apriLightbox(card, video) {
    this.cards.forEach((altra) => {
      const v = altra.querySelector('video');
      if (v && !v.paused) v.pause();
    });

    if (!this.lb) {
      const lb = document.createElement('dialog');
      lb.className = 'gm-ugc__lb';
      lb.innerHTML =
        '<button type="button" class="gm-ugc__lb__x" aria-label="Chiudi">&times;</button>' +
        '<video controls playsinline preload="metadata"></video>' +
        '<div class="gm-ugc__lb__cap" hidden></div>';
      lb.querySelector('.gm-ugc__lb__x').addEventListener('click', () => lb.close());
      lb.addEventListener('click', (e) => {
        if (e.target === lb) lb.close();
      });
      lb.addEventListener('close', () => {
        const v = lb.querySelector('video');
        v.pause();
        v.removeAttribute('src');
        v.load();
      });
      document.body.appendChild(lb);
      this.lb = lb;
    }

    const lb = this.lb;
    const v = lb.querySelector('video');
    const cap = lb.querySelector('.gm-ugc__lb__cap');
    const testo = card.querySelector('.gm-ugc__cap');
    if (testo && testo.textContent.trim()) {
      cap.textContent = testo.textContent.trim();
      cap.hidden = false;
    } else {
      cap.hidden = true;
    }

    if (video.poster) v.poster = video.poster;
    v.muted = false;
    v.src = video.dataset.src || video.src;

    if (typeof lb.showModal === 'function') lb.showModal();
    else lb.setAttribute('open', '');

    const avviato = v.play();
    if (avviato && typeof avviato.catch === 'function') avviato.catch(() => {});
  }

  toggle(card, video) {
    if (!video.paused) {
      this.richiesta = null;
      video.pause();
      return;
    }

    this.richiesta = card;

    // La sorgente vive in data-src finché non serve: così al primo caricamento
    // della pagina non parte nessun download di video.
    if (!video.src && video.dataset.src) video.src = video.dataset.src;

    this.avvia(card, video, true);
  }

  avvia(card, video, riprova) {
    const avviato = video.play();
    if (!avviato || typeof avviato.catch !== 'function') return;

    avviato.catch(() => {
      if (!riprova || this.richiesta !== card) return;

      if (video.readyState >= 3) {
        this.avvia(card, video, false);
        return;
      }

      video.addEventListener(
        'canplay',
        () => {
          if (this.richiesta === card) this.avvia(card, video, false);
        },
        { once: true }
      );
    });
  }
}

if (!customElements.get('gm-ugc')) customElements.define('gm-ugc', GmUgc);
